/**
 * @fileoverview Scout Run Page
 * @description Scorecard for a single imported Scout consolidated report, laid out the way
 * the report itself is: a Summary tab followed by one tab per embedded detail report.
 * @module features/ScoutEval/pages/RunPage
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  ArrowRightLeft,
  ClipboardList,
  FileText,
  Gauge,
  TrendingDown,
} from 'lucide-react';
import { DeltaIndicator, EmptyState, ScoutBreadcrumbs, SummaryMetric } from '../components/common';
import { EvaluatorTable } from '../components/EvaluatorTable';
import { DetailPanel } from '../components/DetailPanel';
import { ReportPanel, ReportTabs } from '../components/ReportTabs';
import { previousRun } from '../utils/scoutStore';
import { useRunDetails } from '../utils/useRunDetails';
import { BAND_COLORS, BAND_LABELS, BAND_STATUS } from '../constants';

const SUMMARY_TAB = 'summary';

/**
 * Render the scorecard for one imported run.
 * @param {Object} props - Stored runs.
 * @returns {JSX.Element} Run page.
 */
export function RunPage({ runs }) {
  const { runId } = useParams();
  const navigate = useNavigate();
  const run = runs.find((item) => item.id === runId);
  const [requestedTab, setRequestedTab] = useState(SUMMARY_TAB);
  const details = useRunDetails(runId);

  const tabs = useMemo(() => [
    { id: SUMMARY_TAB, label: 'Summary' },
    ...(run?.groups || []).map((group) => ({ id: group.id, label: group.label })),
  ], [run]);

  if (!run) {
    return (
      <EmptyState
        icon={FileText}
        title="Run not found"
        message="This report is no longer stored in this browser."
      >
        <Button variant="contained" onClick={() => navigate('/scout-eval')}>Back to runs</Button>
      </EmptyState>
    );
  }

  const baseline = previousRun(runs, run.id);
  const overallDelta = baseline ? run.summary.overallAverage - baseline.summary.overallAverage : null;
  const scoredCount = run.summary.green + run.summary.amber + run.summary.red;
  const activeTab = tabs.some((tab) => tab.id === requestedTab) ? requestedTab : SUMMARY_TAB;
  const activeGroup = run.groups.find((group) => group.id === activeTab) || null;

  return (
    <>
      <ScoutBreadcrumbs trail={[{ label: run.fileName }]} />

      <Paper className="scout-hero" elevation={0} sx={{ '--band-color': BAND_COLORS[run.summary.overallBand] }}>
        <Box className="scout-hero-main">
          <Box className="scout-header-icon"><FileText size={24} /></Box>
          <Box minWidth={0}>
            <Typography className="scout-eyebrow">{run.workspace}</Typography>
            <Typography variant="h4" component="h1" className="scout-hero-title">{run.title}</Typography>
            <Typography className="scout-hero-description">{run.description || run.fileName}</Typography>
            <Stack className="scout-hero-chips" direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {run.generatedAt && <Chip size="small" variant="outlined" label={`Generated ${run.generatedAt}`} />}
              <Chip size="small" variant="outlined" label={`Imported ${new Date(run.importedAt).toLocaleString()}`} />
              {run.sources.map((source) => (
                <Chip
                  key={source.name}
                  size="small"
                  variant="outlined"
                  label={source.rows ? `${source.name} · ${source.rows} rows` : source.name}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        <Box className="scout-hero-score">
          <Typography className="scout-hero-score-label">Overall score</Typography>
          <Typography className="scout-hero-score-value">{run.summary.overallAverage.toFixed(2)}</Typography>
          <Typography className="scout-hero-score-status">{BAND_STATUS[run.summary.overallBand]}</Typography>
          <Box className="scout-hero-score-track">
            <Box
              className="scout-hero-score-fill"
              style={{ width: `${Math.min(100, Math.max(0, run.summary.overallAverage * 100))}%` }}
            />
          </Box>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ArrowRightLeft size={16} />}
            disabled={runs.length < 2}
            onClick={() => navigate(`/scout-eval/compare?current=${run.id}${baseline ? `&baseline=${baseline.id}` : ''}`)}
          >
            Compare run
          </Button>
        </Box>
      </Paper>

      <ReportTabs tabs={tabs} activeId={activeTab} onChange={setRequestedTab} />

      {activeTab === SUMMARY_TAB ? (
        <ReportPanel tabId={SUMMARY_TAB}>
          <Box className="scout-summary-grid" aria-label="Run summary">
            <SummaryMetric
              label="Overall average"
              value={run.summary.overallAverage.toFixed(2)}
              icon={Gauge}
              color="#01426A"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography className="scout-metric-caption">
                  {baseline ? 'vs previous run' : 'First run'}
                </Typography>
                <DeltaIndicator delta={overallDelta} />
              </Stack>
            </SummaryMetric>
            <SummaryMetric
              label="Evaluators scored"
              value={run.summary.evaluatorsScored}
              caption={`${run.summary.notRun} not run`}
              icon={ClipboardList}
              color="#0097A9"
            />
            <SummaryMetric
              label="Below target"
              value={run.summary.belowTarget}
              caption="Score below 0.75"
              icon={TrendingDown}
              color="#C81E3A"
            />
            <Paper className="scout-band-key" elevation={0}>
              <Typography className="scout-metric-label">Score distribution</Typography>
              <Box className="scout-distribution-track" role="img" aria-label={`${run.summary.green} green, ${run.summary.amber} amber, ${run.summary.red} red`}>
                {scoredCount === 0
                  ? <Box className="scout-distribution-empty" />
                  : ['green', 'amber', 'red'].filter((band) => run.summary[band] > 0).map((band) => (
                    <Box
                      key={band}
                      className="scout-distribution-segment"
                      style={{ width: `${(run.summary[band] / scoredCount) * 100}%`, backgroundColor: BAND_COLORS[band] }}
                    />
                  ))}
              </Box>
              <Stack spacing={0.5} mt={1}>
                {Object.entries(BAND_LABELS).map(([band, label]) => (
                  <Stack key={band} direction="row" spacing={1} alignItems="center">
                    <Box className="scout-band-dot" sx={{ backgroundColor: BAND_COLORS[band] }} />
                    <Typography className="scout-band-text">{label}</Typography>
                    <Typography className="scout-band-count">{run.summary[band]}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>

          <EvaluatorTable evaluators={run.evaluators} onOpenDetail={setRequestedTab} />
        </ReportPanel>
      ) : activeGroup && (
        <ReportPanel tabId={activeGroup.id}>
          <DetailPanel
            group={activeGroup}
            content={details && (details[activeGroup.id] || null)}
            onBack={() => setRequestedTab(SUMMARY_TAB)}
          />
        </ReportPanel>
      )}
    </>
  );
}

export default RunPage;
